import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PRODUCT_TYPES } from '../lib/utils';
import {
  Plus,
  AlertTriangle,
  Sparkles,
  Loader2,
  CheckCircle,
  ArrowRight,
  FileText,
  Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service revision prefixes
const SERVICE_REVISIONS = {
  '00': { name: 'Padrão', description: 'Revisão numérica padrão (00, 01, 02...)' },
  'P': { name: 'Pintura', description: 'Serviço de pintura' },
  'Z': { name: 'Zincagem', description: 'Serviço de zincagem' },
  'J': { name: 'Jateamento', description: 'Serviço de jateamento' },
  'OX': { name: 'Oxicorte', description: 'Serviço de oxicorte' },
};

// Types that don't use revision (they don't get the -XX suffix)
const NO_REVISION_TYPES = [2, 3, 4, 7, 9];

export default function CriarCodigo() {
  const navigate = useNavigate();
  const { canCreate } = useAuth();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [similarItems, setSimilarItems] = useState([]);
  const [reviewedItems, setReviewedItems] = useState(new Set()); // Track reviewed items
  const [formData, setFormData] = useState({
    product_type: '',
    family_code: '',
    description: '',
    unit: 'UN',
    notes: '',
    revision_type: '00' // Default to standard revision
  });

  useEffect(() => {
    if (!canCreate()) {
      navigate('/dashboard');
      return;
    }
  }, [canCreate, navigate]);

  // Fetch available families/clients when type changes
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        let url = `${API_URL}/api/admin/categories/available`;
        if (formData.product_type) {
          url += `?type_code=${formData.product_type}`;
        }
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Combine clients and families into a single list
        const combined = [
          ...response.data.clients.map(c => ({ ...c, type: 'cliente' })),
          ...response.data.families.map(f => ({ ...f, type: 'familia' }))
        ];
        setFamilies(combined);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        // Fallback to old endpoint if new one fails
        try {
          const response = await axios.get(`${API_URL}/api/categories/families`);
          setFamilies(response.data);
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      }
    };
    
    fetchCategories();
  }, [formData.product_type]);

  // Debounced similarity check
  const checkSimilarity = useCallback(async () => {
    if (formData.description.length < 5) {
      setSimilarItems([]);
      setReviewedItems(new Set()); // Reset reviewed items
      return;
    }

    setCheckingDuplicates(true);
    setReviewedItems(new Set()); // Reset when checking new description
    try {
      const response = await axios.post(`${API_URL}/api/codes/check-similarity`, {
        description: formData.description,
        product_type: formData.product_type ? parseInt(formData.product_type) : null,
        family_code: formData.family_code ? parseInt(formData.family_code) : null
      });
      setSimilarItems(response.data.similar_items || []);
    } catch (error) {
      console.error('Failed to check similarity:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  }, [formData.description, formData.product_type, formData.family_code]);

  // Trigger similarity check when description changes (only on description change)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.description.length >= 5) {
        checkSimilarity();
      }
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.description]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_type || !formData.family_code || !formData.description) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/codes`, {
        product_type: parseInt(formData.product_type),
        family_code: parseInt(formData.family_code),
        description: formData.description,
        unit: formData.unit,
        notes: formData.notes || null,
        revision_type: formData.revision_type // Send revision type (00, P, Z, J, OX)
      });

      toast.success(`Código ${response.data.code} criado com sucesso!`);
      navigate('/catalogo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar código');
    } finally {
      setLoading(false);
    }
  };

  // Families are now pre-filtered by the backend based on type links
  // Just use them directly - no additional client-side filtering needed
  const filteredFamilies = families;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="criar-codigo-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Criar Novo Código
          </h1>
          <p className="text-slate-500 mt-1">
            Preencha os dados para gerar um novo código de material
          </p>
        </div>

        {/* Similarity Warning */}
        {similarItems.length > 0 && (
          <Card className="similarity-warning border-amber-300 bg-amber-50" data-testid="similarity-warning">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-800 text-base font-heading">
                <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
                Possíveis Duplicatas Encontradas
              </CardTitle>
              <CardDescription className="text-amber-700">
                {reviewedItems.size === similarItems.length ? (
                  <span className="text-green-700">
                    ✓ Todos os itens revisados. Você pode criar o código se tiver certeza que não é duplicata.
                  </span>
                ) : (
                  <>
                    Clique em cada item para marcar como revisado ({reviewedItems.size}/{similarItems.length} revisados)
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {similarItems.map((item, index) => {
                  const isReviewed = reviewedItems.has(item.code);
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-all ${
                        isReviewed 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-white border-amber-200 hover:border-amber-400'
                      }`}
                      onClick={() => {
                        const newReviewed = new Set(reviewedItems);
                        if (isReviewed) {
                          newReviewed.delete(item.code);
                        } else {
                          newReviewed.add(item.code);
                        }
                        setReviewedItems(newReviewed);
                      }}
                      data-testid={`similar-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isReviewed ? 'bg-green-500 border-green-500' : 'border-slate-300'
                        }`}>
                          {isReviewed && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        <code className={`material-code text-sm px-2 py-1 rounded ${
                          isReviewed ? 'bg-green-100 text-green-900' : 'bg-amber-100 text-amber-900'
                        }`}>
                          {item.code}
                        </code>
                        <span className="text-sm text-slate-700">{item.description}</span>
                      </div>
                      <span className={`text-sm font-medium ${
                        isReviewed ? 'text-green-700' : 'text-amber-700'
                      }`}>
                        {isReviewed ? '✓ Revisado' : `${item.similarity_score}% similar`}
                      </span>
                    </div>
                  );
                })}
              </div>
              {reviewedItems.size < similarItems.length && (
                <p className="text-xs text-amber-600 mt-3">
                  Clique nos itens acima para confirmar que você verificou e que não são duplicatas.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              Dados do Material
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Type */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Tipo de Produto *
                </Label>
                <Select
                  value={formData.product_type}
                  onValueChange={(value) => setFormData({ ...formData, product_type: value, family_code: '' })}
                >
                  <SelectTrigger className="h-11 border-slate-200" data-testid="product-type-select">
                    <SelectValue placeholder="Selecione o tipo de produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRODUCT_TYPES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <span className="font-mono text-slate-500 mr-2">{key}</span>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Family/Client Code */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Família / Cliente *
                </Label>
                <Select
                  value={formData.family_code}
                  onValueChange={(value) => setFormData({ ...formData, family_code: value })}
                  disabled={!formData.product_type}
                >
                  <SelectTrigger className="h-11 border-slate-200" data-testid="family-code-select">
                    <SelectValue placeholder="Selecione a família ou cliente" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {filteredFamilies.map((f) => (
                      <SelectItem key={f.code} value={f.code.toString()}>
                        <span className="font-mono text-slate-500 mr-2">{f.code}</span>
                        {f.name}
                        <span className="ml-2 text-xs text-slate-400">
                          ({f.type === 'cliente' ? 'Cliente' : 'Família'})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description with AI check */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Descrição do Material *
                  </Label>
                  {checkingDuplicates && (
                    <span className="flex items-center gap-1 text-xs text-blue-600">
                      <Sparkles className="w-3 h-3 animate-pulse" />
                      Verificando com IA...
                    </span>
                  )}
                  {!checkingDuplicates && formData.description.length >= 5 && similarItems.length === 0 && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="w-3 h-3" />
                      Sem duplicatas
                    </span>
                  )}
                </div>
                <Textarea
                  placeholder="Ex: Parafuso Allen M8x20 Aço Inox"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="min-h-[100px] border-slate-200 focus:border-blue-600"
                  data-testid="description-input"
                />
                <p className="text-xs text-slate-500">
                  A IA irá verificar automaticamente se existem itens similares no sistema.
                </p>
              </div>

              {/* Unit and Notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Unidade
                  </Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger className="h-11 border-slate-200" data-testid="unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UN">UN - Unidade</SelectItem>
                      <SelectItem value="PC">PC - Peça</SelectItem>
                      <SelectItem value="KG">KG - Quilograma</SelectItem>
                      <SelectItem value="M">M - Metro</SelectItem>
                      <SelectItem value="M2">M² - Metro Quadrado</SelectItem>
                      <SelectItem value="L">L - Litro</SelectItem>
                      <SelectItem value="CX">CX - Caixa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    Observações
                  </Label>
                  <Input
                    placeholder="Opcional"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="h-11 border-slate-200"
                    data-testid="notes-input"
                  />
                </div>
              </div>

              {/* Service Revision Type - Only for types that use revision */}
              {formData.product_type && !NO_REVISION_TYPES.includes(parseInt(formData.product_type)) && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Tipo de Revisão / Serviço
                  </Label>
                  <Select
                    value={formData.revision_type}
                    onValueChange={(value) => setFormData({ ...formData, revision_type: value })}
                  >
                    <SelectTrigger className="h-11 border-slate-200" data-testid="revision-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SERVICE_REVISIONS).map(([key, value]) => (
                        <SelectItem key={key} value={key}>
                          <span className="font-mono text-blue-600 mr-2">{key === '00' ? '00' : `${key}0`}</span>
                          {value.name}
                          <span className="ml-2 text-xs text-slate-400">
                            - {value.description}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Selecione o tipo de serviço para códigos de serviço (P=Pintura, Z=Zincagem, J=Jateamento, OX=Oxicorte)
                  </p>
                </div>
              )}

              {/* Preview */}
              {formData.product_type && formData.family_code && (
                <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                    Prévia do Código
                  </p>
                  <code className="material-code text-lg bg-white px-4 py-2 rounded border border-slate-200 inline-block">
                    {formData.product_type}-{formData.family_code.toString().padStart(3, '0')}-XXXXX
                    {!NO_REVISION_TYPES.includes(parseInt(formData.product_type)) && (
                      <>-{formData.revision_type === '00' ? '00' : `${formData.revision_type}0`}</>
                    )}
                  </code>
                  {formData.revision_type !== '00' && !NO_REVISION_TYPES.includes(parseInt(formData.product_type)) && (
                    <p className="text-sm text-blue-600 mt-2">
                      <Wrench className="w-4 h-4 inline mr-1" />
                      Código de serviço: {SERVICE_REVISIONS[formData.revision_type]?.name}
                    </p>
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-200">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/catalogo')}
                  className="text-slate-600"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading || (similarItems.length > 0 && reviewedItems.size < similarItems.length)}
                  className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm btn-active"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Criando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Plus className="w-4 h-4" strokeWidth={1.5} />
                      Criar Código
                    </span>
                  )}
                </Button>
              </div>

              {similarItems.length > 0 && (
                <p className="text-sm text-amber-600 text-center">
                  Verifique os itens similares antes de criar um novo código.
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
